import React from "react";
import { theme, Row, Button, Spin, Skeleton, Modal, Tooltip } from "antd";
import { SearchState } from "../types";
import { Chat } from "./Chat";
import { PureComponent, useEffect, useState } from "react";
import { QuestionInput } from "./inputs/QuestionInput";
import { InfoCircleOutlined, UndoOutlined } from "@ant-design/icons";
import { useApplicationContext } from "src/hooks/useApplication";
import {
  getEntities,
  groupEvents,
  getResultSummariesBulk,
} from "pages/api-clients/graph-search";
import Papa from "papaparse";
import { SnippetV1State } from "pages/api/deep-links/types";
import type { Message, ResultSummaryResponse, FollowUpPrompt } from "./Chat";
import { DefaultPrefixKeys, PrefixGlobalKey } from "pages/api-clients/narrative-prefix";
import { timeframeToString } from "src/utils/date";

export interface ChatSession {
  title: string;
  initialPrompt: string;
  timeframe: Pick<SearchState, "duration" | "startDate" | "endDate">;
  dataSources: Array<{
    query: SearchState;
    format: "csv";
  }>;
}

export function ChatAssistant({
  title,
  titleExtra,
  initialPrompt,
  dataSources,
  timeframe,
  onClose,
  contextLabel,
  inline = false,
  showQueries,
  setShowQueries,
}: {
  title?: string;
  titleExtra?: React.ReactNode;
  initialPrompt: string;
  dataSources: Array<{ query: SearchState; format: "csv" }>;
  timeframe: Pick<SearchState, "duration" | "startDate" | "endDate">;
  onClose(): void;
  contextLabel?: React.ReactNode;
  inline?: boolean;
  showQueries?: boolean;
  setShowQueries?: (show: boolean) => void;
}) {
  const { token } = theme.useToken();
  const orgId = useApplicationContext().org_id!;
  const [loading, setLoading] = useState(false);

  const [localShowQueries, setLocalShowQueries] = useState<boolean>(false);
  const [snippet, setSnippet] = useState<SearchState>({ orgId } as SearchState);
  const [modal, setModal] = useState<JSX.Element | null>(null);

  // Use either controlled or local state for showQueries
  const isShowingQueries = showQueries ?? localShowQueries;
  const toggleQueries = setShowQueries ?? setLocalShowQueries;

  const handleSend = async (
    prompt: string,
    messages: Message[],
    setMessages: (messages: Message[]) => void
  ): Promise<void> => {
    if (loading || !prompt.trim()) return;

    setLoading(true);

    try {
      if (!orgId) {
        throw new Error("Organization ID is not available");
      }

      // Transform queries for the API
      const queries = dataSources.map((source) => ({
        ...source.query,
        orgId,
      }));

      // Debug: Log the final request payload
      const requestPayload = {
        sections: [
          {
            prompt,
            queries,
          },
        ],
      };

      // Send message to the AI and get the response
      const response = (await getResultSummariesBulk(
        requestPayload,
        orgId,
        DefaultPrefixKeys.AGENT
      )) as ResultSummaryResponse;

      // Validate response structure
      if (!response) {
        throw new Error("Empty response from server");
      }

      const botMessage: Message = {
        text: response.summary,
        sender: "bot",
        prompts:
          response.follow_up_prompts?.map((p: FollowUpPrompt) => ({
            title: p.title,
            prompt: p.prompt,
          })) || [],
        copyEnabled: true,
        isStreaming: true,
      };
      setMessages([...messages, botMessage]);
    } catch (error_) {
      console.error("Error getting result summaries:", error_);
      let errorMessage = "Failed to get result summaries";

      if (error_ instanceof Error) {
        if (error_.message.includes("rate limit")) {
          errorMessage = "Rate limit exceeded. Please try again in a moment.";
        } else if (error_.message.includes("Empty response")) {
          errorMessage = "Server returned an empty response. Please try again.";
        } else {
          errorMessage = error_.message;
        }
      }

      const errorBotMessage: Message = {
        text: errorMessage,
        sender: "bot",
        prompts: [],
        copyEnabled: false,
        isStreaming: false,
      };
      setMessages([...messages, errorBotMessage]);
    } finally {
      setLoading(false);
    }
  };

  // Initialize when dataSources change
  useEffect(() => {
    setSnippet({ orgId } as SearchState);

    // Maintain current view state
    if (setShowQueries === undefined) {
      setLocalShowQueries(isShowingQueries);
    }
  }, [dataSources, orgId, setShowQueries, isShowingQueries]);

  const content = (
    <>
      <Row gutter={10} style={{ height: "100%" }}>
        <div style={{ width: "100%", position: "relative", height: "100%" }}>
          <div
            style={{
              display: isShowingQueries ? "none" : "block",
              height: "100%",
            }}
          >
            <Chat
              title={title || "Analysis"}
              dataSources={dataSources}
              snippet={snippet}
              defaultPrompt={initialPrompt}
              handleSend={handleSend}
              loading={loading}
              inline={inline}
              contextLabel={
                <div
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    opacity: 0.8,
                    transition: "all 0.2s ease",
                    justifyContent: "space-between",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = "1";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = "0.8";
                  }}
                  onClick={() => toggleQueries(true)}
                >
                  <span style={{ flex: 1 }}>
                    Analyzing {dataSources.length} data source
                    {dataSources.length === 1 ? "" : "s"} for &ldquo;{title}
                    &rdquo; over time frame of {timeframeToString(timeframe)}
                  </span>
                  <Button
                    type="link"
                    size="small"
                    style={{
                      padding: "0px 12px",
                      height: "auto",
                      backgroundColor: token.colorFillTertiary,
                      border: "none",
                      color: token.colorTextSecondary,
                      transition: "all 0.3s ease",
                      marginLeft: "auto",
                    }}
                  >
                    Preview Data
                  </Button>
                </div>
              }
            />
          </div>
          <div
            style={{
              display: isShowingQueries ? "block" : "none",
              height: "100%",
              background: token.colorBgElevated,
              padding: "16px 24px",
              position: "relative",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  color: token.colorText,
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                Data Preview ({dataSources.length} source
                {dataSources.length === 1 ? "" : "s"})
              </div>
              <Button
                type="link"
                onClick={() => toggleQueries(false)}
                size="small"
                style={{ padding: 0 }}
              >
                Back to Chat
              </Button>
            </div>
            {dataSources.map((source, index) => (
              <div
                key={index}
                style={{
                  marginBottom: index < dataSources.length - 1 ? 16 : 0,
                  width: "100%",
                }}
              >
                <div
                  style={{
                    backgroundColor: token.colorBgContainer,
                    padding: "16px",
                    borderRadius: "8px",
                    width: "100%",
                    border: `1px solid ${token.colorBorderSecondary}`,
                  }}
                >
                  <div style={{ marginBottom: 12 }}>
                    <Tooltip title="Results are limited to the first 5 rows">
                      <InfoCircleOutlined
                        style={{
                          fontSize: 14,
                          color: token.colorTextSecondary,
                          marginRight: 8,
                        }}
                      />
                      <span
                        style={{
                          fontSize: 12,
                          color: token.colorTextSecondary,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                        }}
                      >
                        Source {index + 1}
                      </span>
                    </Tooltip>
                  </div>
                  <QuestionDataPreview searchState={source.query} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </Row>
      {modal}
    </>
  );

  return inline ? (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: token.colorBgElevated,
      }}
    >
      <div style={{ flex: 1, overflow: "auto", padding: "24px 32px" }}>
        {content}
      </div>
    </div>
  ) : (
    <div
      style={{
        position: "relative",
        height: "100%",
        width: "100%",
        backgroundColor: token.colorBgElevated,
        borderLeft: `1px solid ${token.colorBorderSecondary}`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          height: 50,
          width: "100%",
          padding: "0 24px",
          background: token.colorBgElevated,
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
          boxShadow: `${token.boxShadowTertiary}`,
          display: "flex",
          alignItems: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div style={{ fontSize: 16, fontWeight: 500 }}>{title}</div>
            {titleExtra}
          </div>
        </div>
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: "24px 32px" }}>
        {content}
      </div>
    </div>
  );
}
const QuestionDataPreview = ({ searchState }: { searchState: SearchState }) => {
  const orgId = useApplicationContext().org_id!;
  const [data, setData] = useState<Record<string, any>[] | undefined>(
    undefined
  );
  const [headers, setHeaders] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Load data when component mounts or searchState changes
  useEffect(() => {
    if (!orgId) {
      return;
    }

    setLoading(true);
    setError(null);

    const fetchData = async () => {
      try {
        const response = searchState.groupBys?.length
          ? await groupEvents({ ...searchState, format: "csv" }, orgId!, {
              limit: 5,
            })
          : await getEntities({ ...searchState, format: "csv" }, 1, 5, orgId!);

        const parsedData = Papa.parse(response.data as unknown as string, {
          header: true,
        });

        const validData = parsedData.data as Record<string, any>[];
        setData(validData);
        setHeaders(validData.length > 0 ? Object.keys(validData[0]) : []);
      } catch (error_) {
        console.error("Error fetching data:", error_);
        let errorMessage = "Failed to fetch data";
        if (error_ instanceof Error) {
          errorMessage = error_.message;
          // Add more specific error handling for known error types
          if (error_.message.includes("404")) {
            errorMessage = "Data source not found";
          } else if (error_.message.includes("401")) {
            errorMessage = "Not authorized to access this data";
          }
        }
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [orgId, JSON.stringify(searchState)]); // Use stringified version to catch all changes

  return (
    <div style={{ width: "100%", overflowX: "auto" }}>
      {loading && <Skeleton active />}
      {error && (
        <div style={{ color: "red", marginBottom: "8px" }}>Error: {error}</div>
      )}
      {!loading && !error && data && data.length === 0 && (
        <>No data returned. Please adjust your query to get results.</>
      )}
      {!loading && !error && data && data.length > 0 && (
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "14px",
          }}
        >
          <thead>
            <tr>
              {headers.map((header, index) => (
                <th
                  key={index}
                  style={{
                    padding: "8px",
                    textAlign: "left",
                    borderBottom: "1px solid rgba(255, 255, 255, 0.15)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {headers.map((header, colIndex) => (
                  <td
                    key={colIndex}
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row[header]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const QuestionEditorModal = ({
  searchState,
  onChange,
  onReset,
  onClose,
}: {
  searchState: SearchState;
  onChange(searchState: SearchState): void;
  onReset(): void;
  onClose(): void;
}) => {
  return (
    <Modal open={true} onCancel={onClose}>
      <PureQueryInput
        serialized={JSON.stringify(searchState)}
        onChange={onChange}
        onReset={onReset}
      />
    </Modal>
  );
};

export class PureQueryInput extends PureComponent<{
  serialized: string;
  onChange(searchState: SearchState): void;
  onReset(): void;
}> {
  lastRun: { when: number; serial: string };
  constructor(props: {
    serialized: string;
    onChange(searchState: SearchState): void;
    onReset(): void;
  }) {
    /* console.log("constructing"); */
    super(props);
    this.lastRun = {
      when: Date.now(),
      serial: props.serialized,
    };
  }

  render() {
    this.lastRun = { when: Date.now(), serial: this.props.serialized };
    const value = JSON.parse(this.props.serialized) as SearchState;
    return (
      <QuestionInput
        alternateStyle
        seed={this.props.serialized}
        value={value}
        resetTitle="Reset to defaults"
        resetIcon={<UndoOutlined style={{ fontSize: 14 }} />}
        hideDisabled
        onChange={this.props.onChange}
        handleReset={this.props.onReset}
        options={{
          disableGroupBys: true,
          disableMeasures: true,
          disableSelect: true,
          disableSearchTerm: true,
          disableCompareWith: true,
          disableFormula: true,
          disableHaving: true,
          disableSnippets: false,
          disableSuggestions: false,
          addFromTeamSnippet: false,
        }}
        snippetTransformer={(state) =>
          ({
            duration: state.duration,
            startDate: state.startDate,
            endDate: state.endDate,
            filters: state.filters,
          } as SearchState)
        }
        snippetOptionFilter={(snippet) => {
          const json = snippet?.json as SnippetV1State | undefined;
          return (json?.params?.filters?.length || 0) > 0;
        }}
      />
    );
  }
}
